import { hourTicks, stepForSpacing, TICK_STEPS } from './charts.ts';
import { trackChartPointer } from './chartPointer.ts';
import { gridLineBudget, gridTicks, tickDecimals } from './lib/gridTicks.ts';
import { compactTime } from './tideChart.ts';
import type { CurrentCurve } from './sources/currents.ts';

/**
 * Predicted tidal stream over 36 hours.
 *
 * Velocity is signed about slack water, so this is a diverging encoding, not a
 * sequential one: flood and ebb are genuine opposites and slack is a real zero.
 * It uses the documented diverging pair — blue and red about a neutral midpoint
 * — and never colour alone: the zero line is labelled, each arm is named in the
 * legend, and the maxima are directly labelled with their speed and time.
 */

const W = 336;
const PLOT_H = 104;
const PAD = { top: 16, right: 14, bottom: 20, left: 36 };

const INK_MUTED = '#898781';
const INK_PRIMARY = '#ffffff';
const GRID = '#2c2c2a';
const AXIS = '#383835';
const SURFACE = '#1a1a19';

/** Documented diverging poles: cool floods in, warm ebbs out. */
export const FLOOD_COLOR = '#3987e5';
export const EBB_COLOR = '#e66767';

const SVG_NS = 'http://www.w3.org/2000/svg';

function el(name: string, attrs: Record<string, string | number> = {}): SVGElement {
  const node = document.createElementNS(SVG_NS, name);
  for (const [k, v] of Object.entries(attrs)) node.setAttribute(k, String(v));
  return node;
}

/** Signed velocity at an arbitrary time, interpolated between samples. */
export function velocityAt(curve: CurrentCurve, at: Date): number | null {
  const pts = curve.points;
  if (pts.length === 0) return null;
  const ms = at.getTime();
  if (ms <= pts[0].t.getTime()) return pts[0].v;
  if (ms >= pts[pts.length - 1].t.getTime()) return pts[pts.length - 1].v;

  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1];
    const b = pts[i];
    if (ms <= b.t.getTime()) {
      const span = b.t.getTime() - a.t.getTime();
      const f = span === 0 ? 0 : (ms - a.t.getTime()) / span;
      return a.v + (b.v - a.v) * f;
    }
  }
  return null;
}

/** Slack is treated as a band, not a point — below this the set is meaningless. */
export const SLACK_KT = 0.1;

export interface StreamAt {
  /** Signed knots: positive floods. */
  velocityKt: number;
  /** Speed through the water, always positive. */
  speedKt: number;
  /** Direction the stream sets TOWARD, or null at slack. */
  setDeg: number | null;
  phase: 'flood' | 'ebb' | 'slack';
}

/**
 * The stream at one moment: how hard it runs and which way it sets.
 *
 * Direction comes from the sign — a flooding stream sets on the station's mean
 * flood bearing, an ebbing one on its ebb bearing — which is what lets the
 * arrow follow the cursor across the chart.
 */
export function streamAt(curve: CurrentCurve, at: Date): StreamAt | null {
  const v = velocityAt(curve, at);
  if (v === null) return null;

  if (Math.abs(v) < SLACK_KT) {
    return { velocityKt: v, speedKt: Math.abs(v), setDeg: null, phase: 'slack' };
  }
  return {
    velocityKt: v,
    speedKt: Math.abs(v),
    setDeg: v > 0 ? curve.floodDir : curve.ebbDir,
    phase: v > 0 ? 'flood' : 'ebb',
  };
}

export function renderCurrentChart(
  host: HTMLElement,
  curve: CurrentCurve,
  onHover: (at: Date | null) => void,
  now: Date = new Date(),
): void {
  host.textContent = '';

  const pts = curve.points;
  if (pts.length < 2) {
    const empty = document.createElement('p');
    empty.className = 'chart-empty';
    empty.textContent = 'No current prediction available.';
    host.append(empty);
    return;
  }

  const innerW = W - PAD.left - PAD.right;
  const t0 = pts[0].t.getTime();
  const t1 = pts[pts.length - 1].t.getTime();
  const span = Math.max(t1 - t0, 1);

  // Symmetric about zero so flood and ebb are directly comparable by eye.
  const peak = Math.max(...pts.map((p) => Math.abs(p.v)));
  const bound = Math.ceil((peak + 0.5) * 2) / 2;

  const xAt = (ms: number) => PAD.left + ((ms - t0) / span) * innerW;
  const yAt = (v: number) => PAD.top + (1 - (v + bound) / (2 * bound)) * PLOT_H;
  const zeroY = yAt(0);

  const totalH = PAD.top + PLOT_H + PAD.bottom;
  const svg = el('svg', {
    viewBox: `0 0 ${W} ${totalH}`,
    width: '100%',
    height: totalH,
    role: 'img',
    'aria-label': 'Predicted tidal stream over the next 33 hours',
  });

  // --- Frame: a grid at round speeds. Zero is slack water and falls inside a
  // symmetric axis, so it is always one of the lines. ---
  const ticks = gridTicks(-bound, bound, gridLineBudget(PLOT_H));
  const decimals = tickDecimals(ticks);
  for (const v of ticks) {
    svg.append(el('line', {
      x1: PAD.left, x2: W - PAD.right, y1: yAt(v), y2: yAt(v),
      stroke: GRID, 'stroke-width': 1,
    }));
    const label = el('text', {
      x: PAD.left - 6, y: yAt(v) + 3.5,
      'text-anchor': 'end', fill: INK_MUTED, 'font-size': 9,
      'font-variant-numeric': 'tabular-nums',
    });
    label.textContent = v.toFixed(decimals);
    svg.append(label);
  }

  // Slack water: the neutral midpoint of the diverging scale.
  svg.append(el('line', {
    x1: PAD.left, x2: W - PAD.right, y1: zeroY, y2: zeroY,
    stroke: AXIS, 'stroke-width': 1,
  }));
  const slackLabel = el('text', {
    x: PAD.left - 6, y: zeroY + 3.5,
    'text-anchor': 'end', fill: INK_MUTED, 'font-size': 9,
  });
  slackLabel.textContent = '0';
  svg.append(slackLabel);

  // --- Hour ticks ---
  const tickStep = stepForSpacing(span, innerW, 7, TICK_STEPS);
  const labelStep = stepForSpacing(span, innerW, 46, TICK_STEPS.filter((h) => h >= tickStep));

  for (const ms of hourTicks(t0, t1, tickStep)) {
    const tx = xAt(ms);
    const at = new Date(ms);
    const labelled = at.getHours() % labelStep === 0;

    if (labelled) {
      svg.append(el('line', {
        x1: tx, x2: tx, y1: PAD.top, y2: PAD.top + PLOT_H,
        stroke: GRID, 'stroke-width': 1,
      }));
    }
    svg.append(el('line', {
      x1: tx, x2: tx,
      y1: PAD.top + PLOT_H, y2: PAD.top + PLOT_H + (labelled ? 6 : 3),
      stroke: INK_MUTED, 'stroke-width': 1, opacity: labelled ? 1 : 0.75,
    }));

    if (!labelled) continue;
    const text = el('text', {
      x: tx, y: PAD.top + PLOT_H + 15,
      'text-anchor': 'middle', fill: INK_MUTED, 'font-size': 9,
    });
    text.textContent = at.getHours() === 0
      ? new Intl.DateTimeFormat([], { weekday: 'short' }).format(at)
      : new Intl.DateTimeFormat([], { hour: 'numeric' }).format(at);
    svg.append(text);
  }

  // --- The stream, filled to slack and split at the zero crossing ---
  const clipTop = el('clipPath', { id: 'clip-flood' });
  clipTop.append(el('rect', {
    x: PAD.left, y: PAD.top, width: innerW, height: Math.max(zeroY - PAD.top, 0),
  }));
  const clipBottom = el('clipPath', { id: 'clip-ebb' });
  clipBottom.append(el('rect', {
    x: PAD.left, y: zeroY, width: innerW, height: Math.max(PAD.top + PLOT_H - zeroY, 0),
  }));
  const defs = el('defs');
  defs.append(clipTop, clipBottom);
  svg.append(defs);

  const line = pts.map((p) => `${xAt(p.t.getTime())},${yAt(p.v)}`).join(' ');
  const area = `${xAt(t0)},${zeroY} ${line} ${xAt(t1)},${zeroY}`;

  for (const [clip, color] of [['clip-flood', FLOOD_COLOR], ['clip-ebb', EBB_COLOR]] as const) {
    svg.append(el('polygon', {
      points: area, fill: color, 'fill-opacity': 0.28, 'clip-path': `url(#${clip})`,
    }));
    svg.append(el('polyline', {
      points: line, fill: 'none', stroke: color, 'stroke-width': 2,
      'stroke-linejoin': 'round', 'clip-path': `url(#${clip})`,
    }));
  }

  // --- Maxima, directly labelled; slacks marked on the zero line ---
  for (const e of curve.events) {
    const ex = xAt(e.t.getTime());
    if (ex < PAD.left || ex > W - PAD.right) continue;

    if (e.kind === 'slack') {
      svg.append(el('circle', {
        cx: ex, cy: zeroY, r: 2.5, fill: INK_MUTED, stroke: SURFACE, 'stroke-width': 1.5,
      }));
      continue;
    }

    const ey = yAt(e.velocityKt);
    const above = e.velocityKt > 0;
    svg.append(el('circle', {
      cx: ex, cy: ey, r: 3.5,
      fill: above ? FLOOD_COLOR : EBB_COLOR, stroke: SURFACE, 'stroke-width': 2,
    }));

    const clampX = Math.min(Math.max(ex, PAD.left + 16), W - PAD.right - 16);
    const value = el('text', {
      x: clampX, y: above ? ey - 8 : ey + 15,
      'text-anchor': 'middle', fill: INK_PRIMARY, 'font-size': 9, 'font-weight': 600,
    });
    value.textContent = `${Math.abs(e.velocityKt).toFixed(1)}`;
    svg.append(value);

    const time = el('text', {
      x: clampX, y: above ? ey - 18 : ey + 25,
      'text-anchor': 'middle', fill: INK_MUTED, 'font-size': 8,
    });
    time.textContent = compactTime(e.t);
    svg.append(time);
  }

  // --- Now ---
  const nowMs = now.getTime();
  if (nowMs >= t0 && nowMs <= t1) {
    const nx = xAt(nowMs);
    svg.append(el('line', {
      x1: nx, x2: nx, y1: PAD.top, y2: PAD.top + PLOT_H,
      stroke: INK_PRIMARY, 'stroke-width': 1, opacity: 0.55,
    }));
    const nowLabel = el('text', {
      x: Math.min(Math.max(nx, PAD.left + 12), W - PAD.right - 12),
      y: PAD.top - 5, 'text-anchor': 'middle', fill: INK_MUTED, 'font-size': 8,
    });
    nowLabel.textContent = 'now';
    svg.append(nowLabel);
  }

  // --- Hover: crosshair, cursor dot, and the arrow above follows along ---
  const crosshair = el('line', {
    y1: PAD.top, y2: PAD.top + PLOT_H,
    stroke: INK_MUTED, 'stroke-width': 1, opacity: 0,
  });
  const cursor = el('circle', { r: 4, stroke: SURFACE, 'stroke-width': 2, opacity: 0 });
  svg.append(crosshair, cursor);

  const wrap = document.createElement('div');
  wrap.className = 'chart-wrap';
  wrap.append(svg);

  const show = (ms: number) => {
    const clamped = Math.min(Math.max(ms, t0), t1);
    const at = new Date(clamped);
    const s = streamAt(curve, at);
    if (!s) return;

    const tx = xAt(clamped);
    crosshair.setAttribute('x1', String(tx));
    crosshair.setAttribute('x2', String(tx));
    crosshair.setAttribute('opacity', '1');
    cursor.setAttribute('cx', String(tx));
    cursor.setAttribute('cy', String(yAt(s.velocityKt)));
    cursor.setAttribute('fill', s.phase === 'ebb' ? EBB_COLOR : FLOOD_COLOR);
    cursor.setAttribute('opacity', '1');

    onHover(at);
  };

  const hide = () => {
    crosshair.setAttribute('opacity', '0');
    cursor.setAttribute('opacity', '0');
    onHover(null);
  };

  trackChartPointer(svg, {
    onMove: (clientX) => {
      const box = svg.getBoundingClientRect();
      const px = ((clientX - box.left) / box.width) * W;
      const frac = Math.min(Math.max((px - PAD.left) / innerW, 0), 1);
      show(t0 + frac * span);
    },
    onEnd: hide,
  });

  host.append(wrap);

  const legend = document.createElement('p');
  legend.className = 'chart-legend';
  legend.innerHTML =
    `<span class="legend-item"><i class="swatch-line" style="background:${FLOOD_COLOR}"></i>Flood (in)</span>` +
    `<span class="legend-item"><i class="swatch-line" style="background:${EBB_COLOR}"></i>Ebb (out)</span>` +
    `<span class="legend-item"><i class="swatch-slack"></i>Slack</span>`;
  host.append(legend);
}

/** The table twin: slack and maximum events, readable without the chart. */
export function renderCurrentTable(host: HTMLElement, curve: CurrentCurve): void {
  host.textContent = '';
  const fmt = new Intl.DateTimeFormat([], {
    weekday: 'short', hour: 'numeric', minute: '2-digit',
  });

  const table = document.createElement('table');
  table.className = 'reading-table';
  table.innerHTML =
    '<thead><tr><th scope="col">Time</th><th scope="col">Stream</th>' +
    '<th scope="col">Speed</th><th scope="col">Sets</th></tr></thead>';

  const body = document.createElement('tbody');
  for (const e of curve.events) {
    const setDeg = e.kind === 'slack' ? null : e.velocityKt > 0 ? curve.floodDir : curve.ebbDir;
    const row = document.createElement('tr');
    row.innerHTML =
      `<td>${fmt.format(e.t)}</td>` +
      `<td>${e.kind === 'slack' ? 'Slack' : e.kind === 'flood' ? 'Max flood' : 'Max ebb'}</td>` +
      `<td>${e.kind === 'slack' ? '—' : `${Math.abs(e.velocityKt).toFixed(1)} kt`}</td>` +
      `<td>${setDeg === null ? '—' : `${Math.round(setDeg)}°`}</td>`;
    body.append(row);
  }
  table.append(body);
  host.append(table);
}
